import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API URL from environment variable
  const API_URL = process.env.REACT_APP_API_URL;

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ submitted: false, error: false, message: '' });

    // Validation
    if (!formData.name.trim()) {
      setFormStatus({
        submitted: false,
        error: true,
        message: 'Please enter your name'
      });
      setIsSubmitting(false);
      return;
    }

    if (!formData.email.trim()) {
      setFormStatus({
        submitted: false,
        error: true,
        message: 'Please enter your email'
      });
      setIsSubmitting(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus({
        submitted: false,
        error: true,
        message: 'Please enter a valid email address'
      });
      setIsSubmitting(false);
      return;
    }

    if (!formData.message.trim()) {
      setFormStatus({
        submitted: false,
        error: true,
        message: 'Please enter your message'
      });
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 5000) {
      setFormStatus({
        submitted: false,
        error: true,
        message: 'Message is too long. Maximum 5000 characters.'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Send to backend API
      const response = await axios.post(`${API_URL}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      });

      if (response.data.success) {
        // Success
        setFormStatus({
          submitted: true,
          error: false,
          message: response.data.message || 'Thank you for contacting us! We\'ll get back to you within 24 hours.'
        });

        // Reset form
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });

        // Clear success message after 5 seconds
        setTimeout(() => {
          setFormStatus({ submitted: false, error: false, message: '' });
        }, 5000);
      }

    } catch (error) {
      console.error('Contact form error:', error);
      
      let errorMessage = 'Something went wrong. Please try again later.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message === 'Network Error') {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      }

      setFormStatus({
        submitted: false,
        error: true,
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50">
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-primary-600 to-primary-700 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-accent-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white leading-tight mb-6">
              Get In Touch
            </h1>
            <p className="text-xl md:text-2xl text-primary-50 font-light">
              Have questions or need customized furniture? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 relative">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
            
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-8">
              
              {/* Email */}
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-dark-900 mb-2">Email Us</h3>
                    <a 
                      href="mailto:info@skyfurnitureca.com"
                      className="text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      skyfurniturecanada@gmail.com
                    </a>
                    <p className="text-sm text-dark-500 mt-2">
                      We'll respond within 24 hours
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-accent-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-dark-900 mb-2">Call Us</h3>
                    <a 
                      href="tel:+15551234567"
                      className="text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      +1 (647) 872-6713
                    </a>
                    <p className="text-sm text-dark-500 mt-2">
                      24/7 Customer Support
                    </p>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-dark-900 mb-2">Visit Us</h3>
                    <p className="text-dark-600">
                      Greater Toronto Area(GTA)<br />
                      Windsor-Ottawa-London<br />
                      Niagra-Waterloo-Barrie
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3 className="font-display font-bold text-xl text-dark-900 mb-4">
                  Business Hours
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-dark-600">Monday - Friday</span>
                    <span className="font-semibold text-dark-900">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-dark-600">Saturday</span>
                    <span className="font-semibold text-dark-900">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-dark-600">Sunday</span>
                    <span className="font-semibold text-dark-900">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100">
                
                <div className="mb-8">
                  <h2 className="text-3xl md:text-4xl font-display font-bold text-dark-900 mb-3">
                    Send Us a Message
                  </h2>
                  <p className="text-lg text-dark-600">
                    Fill out the form below and we'll get back to you as soon as possible.
                  </p>
                </div>

                {/* Success/Error Messages */}
                {formStatus.message && (
                  <div className={`mb-6 p-4 rounded-xl flex items-start gap-3 animate-slide-up ${
                    formStatus.error 
                      ? 'bg-red-50 border border-red-200' 
                      : 'bg-green-50 border border-green-200'
                  }`}>
                    {formStatus.error ? (
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    )}
                    <p className={`text-sm ${
                      formStatus.error ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {formStatus.message}
                    </p>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  
                  {/* Message Textarea */}
                  <div>
                    <label 
                      htmlFor="message"
                      className="block text-sm font-semibold text-dark-700 mb-2"
                    >
                      Your Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your inquiry, questions, or custom furniture requirements..."
                      rows="8"
                      maxLength="5000"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-dark-900 placeholder-dark-400 resize-y min-h-[200px]"
                      required
                    />
                    <div className="mt-2 flex justify-between items-center">
                      <p className="text-xs text-dark-500">
                        Maximum 5000 characters
                      </p>
                      <p className="text-xs text-dark-500">
                        {formData.message.length.toLocaleString()} 
                      </p>
                    </div>
                  </div>

                  {/* Name Input */}
                  <div>
                    <label 
                      htmlFor="name"
                      className="block text-sm font-semibold text-dark-700 mb-2"
                    >
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Please Enter Your Name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-dark-900 placeholder-dark-400"
                      required
                    />
                  </div>

                  {/* Email Input */}
                  <div>
                    <label 
                      htmlFor="email"
                      className="block text-sm font-semibold text-dark-700 mb-2"
                    >
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Please Enter Your Email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-dark-900 placeholder-dark-400"
                      required
                    />
                  </div>

                  {/* Phone Input (Optional) */}
                  <div>
                    <label 
                      htmlFor="phone"
                      className="block text-sm font-semibold text-dark-700 mb-2"
                    >
                      Phone Number (Optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-dark-900 placeholder-dark-400"
                    />
                  </div>

                  {/* Subject Input (Optional) */}
                  <div>
                    <label 
                      htmlFor="subject"
                      className="block text-sm font-semibold text-dark-700 mb-2"
                    >
                      Subject (Optional)
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="What is this regarding?"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-dark-900 placeholder-dark-400"
                    />
                  </div>
                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full flex items-center justify-center gap-3 px-8 py-4 bg-primary-600 text-white text-lg font-semibold rounded-xl transition-all duration-300 transform ${
                      isSubmitting 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:bg-primary-700 hover:scale-[1.02] hover:shadow-2xl shadow-xl'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>

                {/* Privacy Note */}
                <p className="mt-6 text-sm text-dark-500 text-center">
                  We respect your privacy. Your information will never be shared with third parties.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-16 bg-primary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-dark-900 mb-4">
              Why Choose Skyfurniture?
            </h2>
            <p className="text-lg text-dark-600 mb-12">
              We're committed to providing the best furniture and customer service in Canada
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="text-4xl mb-4">🚚</div>
                <h3 className="font-bold text-xl text-dark-900 mb-2">Fast Delivery</h3>
                <p className="text-dark-600">Less than two working days</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="text-4xl mb-4">✨</div>
                <h3 className="font-bold text-xl text-dark-900 mb-2">Premium Quality</h3>
                <p className="text-dark-600">Handcrafted furniture built to last</p>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="text-4xl mb-4">💯</div>
                <h3 className="font-bold text-xl text-dark-900 mb-2">Satisfaction Guaranteed</h3>
                <p className="text-dark-600">30-day money-back guarantee</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;